import {
  Types,
} from '@jumpcloud/ui-components';
import { get } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import RemoteAssistApi from 'client/api/v2/RemoteAssistApi';
import RouteGuardMixin from 'client/AdminPortal/router/guards/RouteGuardMixin';

const { SecondaryButtonProps } = Types;
const secondaryButtonProps = SecondaryButtonProps({ hasNoBackground: true });
const settingsModel = 'SettingsModel';

export default {
  mixins: [
    RouteGuardMixin,
  ],

  data: () => ({
    changedRemoteAssistSettings: false,
    feedbackEnabled: false,
    loading: false,
    rasEnabled: false,
    sessionTimeout: 120,
    showModalUndo: false,
  }),

  secondaryButtonProps,

  computed: {
    ...mapState(settingsModel, [
      'item',
      'previousItem',
    ]),

    ...mapGetters(settingsModel, ['hasItemChanged']),

    orgSettings: {
      get() {
        return this.item;
      },
      set(value) {
        this.updateItem(value);
      },
    },

    enableMainAction() {
      return this.hasItemChanged || this.changedRemoteAssistSettings;
    },
  },

  mounted() {
    this.handleMounted();
  },

  methods: {
    ...mapActions(settingsModel, [
      'fetchItem',
      'resetItem',
      'saveItem',
      'updatePreviousItem',
      'updateItem',
      'unset',
    ]),

    ...mapActions(notificationModule, [
      'triggerErrorNotification',
      'triggerSuccessNotification',
    ]),

    handleCancel() {
      if (this.hasItemChanged) {
        this.showModalUndo = true;
      }
    },

    async handleContinueUndo() {
      this.showModalUndo = false;
      await this.resetModel();
    },

    handleCloseModalUndo() {
      this.showModalUndo = false;
    },

    handleMounted() {
      this.loading = true;

      this.fetchItem()
        .catch(() => {
          this.triggerErrorNotification({
            title: 'Error',
            message: 'Unable to load settings.',
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    async handleSave() {
      this.loading = true;
      try {
        if (this.changedRemoteAssistSettings) {
          await this.saveRemoteAssistSettings();
        }
        await this.saveOrgSettings();
      } finally {
        this.loading = false;
      }
    },

    hasModelChanged() {
      // Required for RouteGuardMixin
      return this.hasItemChanged;
    },

    async resetModel() {
      const { previousItem } = this;

      this.resetItem();

      await this.updateItem(previousItem);
      await this.updatePreviousItem(previousItem);
    },

    saveOrgSettings() {
      if (!this.hasItemChanged) return Promise.resolve();

      this.updateItem(this.item);

      return this.saveItem()
        .then(() => {
          this.triggerSuccessNotification({
            title: 'Saved',
            message: 'Settings have been updated.',
          });
        })
        .catch((err) => {
          const message = get(err, 'response.data.message', 'Unable to save settings.');

          this.triggerErrorNotification({ title: 'Error', message });
        });
    },

    changeRemoteAssistSettings(feedbackEnabled, rasEnabled, sessionTimeout, silentEnabled) {
      this.feedbackEnabled = feedbackEnabled;
      this.rasEnabled = rasEnabled;
      this.sessionTimeout = sessionTimeout;
      this.changedRemoteAssistSettings = true;
      this.silentEnabled = silentEnabled;
    },

    async saveRemoteAssistSettings() {
      try {
        await RemoteAssistApi.postConfig({
          feedbackEnabled: this.feedbackEnabled,
          enabled: this.rasEnabled,
          sessionTimeout: this.sessionTimeout * 60,
          silentAssistEnabled: this.silentEnabled,
        });
        this.triggerSuccessNotification({
          title: 'Saved',
          message: 'Remote Assist Settings have been updated.',
        });
        this.changedRemoteAssistSettings = false;
      } catch (err) {
        const message = get(err, 'response.data.message', 'Unable to save Remote Assist settings.');
        this.triggerErrorNotification({ title: 'Error', message });
      }
    },
  },
};
